import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BASELINE } from '../../util';
import { useParams } from 'react-router-dom';

const QuizEditModal = ({
    isOpen,
    onClose,
    htmlContent,
    qType = "MCQ",
    subTopicText = "algebra",
}) => {
    // URL에서 cid와 lid 추출 (예: /dashboard/dev/class/6/lesson/edit/17)
    const { cid, lid } = useParams();
    // Subject 선택 (math 또는 english; 기본 math)
    const [subject, setSubject] = useState("math");

    // States
    const [result, setResult] = useState('');
    const [quizData, setQuizData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentView, setCurrentView] = useState('json'); // 'json', 'cards', or 'existing'
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [isKatexRendered, setIsKatexRendered] = useState(false);
    const [isEditingRaw, setIsEditingRaw] = useState(false);
    const [showSubmitButton, setShowSubmitButton] = useState(false);
    const [existingQuizzes, setExistingQuizzes] = useState([]);
    const [isLoadingExisting, setIsLoadingExisting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // 진행 상황 관련 상태
    const [processingStatus, setProcessingStatus] = useState('');
    const [chunksStatus, setChunksStatus] = useState([]);
    const [combinedResults, setCombinedResults] = useState([]);
    const [numChunks, setNumChunks] = useState(0);
    const [manualChunkCount, setManualChunkCount] = useState(3); // 기본값 3개 청크
    const [useManualChunks, setUseManualChunks] = useState(false);
    
    // 타겟 문제 갯수 관련 상태
    const [targetQuestionCount, setTargetQuestionCount] = useState(28); // 기본값 28문제
    const [useTargetCount, setUseTargetCount] = useState(false);
    const [questionsPerChunk, setQuestionsPerChunk] = useState(0); // 계산된 청크당 문제 수

    // Refs for content that needs KaTeX rendering
    const cardRef = useRef(null);

    // 청크 수와 타겟 문제 수가 변경될 때 청크당 문제 수 계산
    useEffect(() => {
        if (useManualChunks && useTargetCount && manualChunkCount > 0) {
            const perChunk = Math.ceil(targetQuestionCount / manualChunkCount);
            setQuestionsPerChunk(perChunk);
        } else {
            setQuestionsPerChunk(0);
        }
    }, [manualChunkCount, targetQuestionCount, useManualChunks, useTargetCount]);

    // 자동으로 HTML을 적절한 크기의 청크로 나눔
    const splitHtmlIntoChunks = (html, maxChunkSize = 4000) => {
        // 너무 짧으면 분할 안함
        if (html.length <= maxChunkSize) {
            return [html];
        }

        const chunks = [];
        let startIndex = 0;

        while (startIndex < html.length) {
            // 다음 청크 끝 계산
            let endIndex = Math.min(startIndex + maxChunkSize, html.length);
            
            // 태그 경계에서 끊도록 조정
            if (endIndex < html.length) {
                // 청크 끝 부분에서 완성된 태그 찾기
                const searchRegion = html.substring(Math.max(endIndex - 500, startIndex), endIndex);
                const lastTagEnd = searchRegion.lastIndexOf(">");
                
                if (lastTagEnd !== -1) {
                    // 완전한 태그 끝에서 끊기
                    endIndex = Math.max(endIndex - 500, startIndex) + lastTagEnd + 1;
                }
            }
            
            chunks.push(html.substring(startIndex, endIndex));
            startIndex = endIndex;
        }
        
        return chunks;
    };

    // 수동으로 HTML을 N개의 동일한 크기 청크로 나눔
    const splitHtmlIntoEqualChunks = (html, numChunks = 3) => {
        if (numChunks <= 1) return [html];
        
        const chunkSize = Math.ceil(html.length / numChunks);
        const chunks = [];
        
        for (let i = 0; i < numChunks; i++) {
            const startIndex = i * chunkSize;
            let endIndex = Math.min((i + 1) * chunkSize, html.length);
            
            // 마지막 청크가 아닐 경우, 가능하면 태그 끝에서 끊도록 시도
            if (i < numChunks - 1) {
                const searchRegion = html.substring(Math.max(endIndex - 500, startIndex), endIndex);
                const lastTagEnd = searchRegion.lastIndexOf(">");
                
                if (lastTagEnd !== -1) {
                    endIndex = Math.max(endIndex - 500, startIndex) + lastTagEnd + 1;
                }
            }
            
            chunks.push(html.substring(startIndex, endIndex));
        }
        
        return chunks;
    };

    // Fetch existing quizzes for current skill_descs (lid)
    const fetchExistingQuizzes = async () => {
        setIsLoadingExisting(true);
        try {
            const response = await axios.post(`${BASELINE}select/gen/topicai`, { skill_descs: lid });
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                // Store raw data and create display version
                const formattedData = response.data.data.map(item => {
                    // Create display version of the quiz
                    return {
                        // Keep original DB record for update/delete
                        originalData: item,
                        // ID for operations - using questionIds from database
                        id: item.questionIds,
                        // Display fields mapped to our component format
                        stem: item.stem || "",
                        a: item.a || item.A || "",
                        b: item.b || item.B || "",
                        c: item.c || item.C || "",
                        d: item.d || item.D || "",
                        correct_answer: item.correct_answer || item.correct_ans || "",
                        rationale: item.rationale || ""
                    };
                });

                setExistingQuizzes(formattedData);
                if (formattedData.length > 0) {
                    setCurrentCardIndex(0);
                    setCurrentView('existing');
                } else {
                    alert("No existing quizzes found for this lesson.");
                }
            } else {
                alert("Failed to fetch existing quizzes or none were found.");
            }
        } catch (error) {
            console.error("Error fetching existing quizzes:", error);
            alert(`Error fetching existing quizzes: ${error.message}`);
        } finally {
            setIsLoadingExisting(false);
        }
    };

    // Update an existing quiz by sending the entire object
    const updateExistingQuiz = async (updatedQuizDisplay) => {
        if (!updatedQuizDisplay || !updatedQuizDisplay.id) {
            alert("Cannot update quiz: missing ID");
            return;
        }

        setIsSaving(true);
        try {
            // Get the original data
            const originalData = updatedQuizDisplay.originalData || {};

            // Create updated object with only the fields we want to change
            // instead of sending the entire object with all fields
            const updateFields = {
                stem: updatedQuizDisplay.stem,
                a: updatedQuizDisplay.a,
                b: updatedQuizDisplay.b,
                c: updatedQuizDisplay.c,
                d: updatedQuizDisplay.d,
                correct_answer: updatedQuizDisplay.correct_answer,
                rationale: updatedQuizDisplay.rationale
            };

            // Send update request to the API
            const response = await axios.post(`${BASELINE}select/gen/topicai`, {
                questionIds: [updatedQuizDisplay.id],
                operation: "update",
                updateFields: updateFields
            });

            if (response.data && response.data.success) {
                // Update local state with the newly updated data
                const updatedQuizzes = [...existingQuizzes];
                updatedQuizzes[currentCardIndex] = {
                    ...updatedQuizDisplay,
                    originalData: {
                        ...originalData,
                        ...updateFields // Update the originalData with changes
                    }
                };
                setExistingQuizzes(updatedQuizzes);
                alert("Quiz updated successfully!");
            } else {
                alert("Failed to update quiz: " + (response.data?.message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error updating quiz:", error);
            alert(`Error updating quiz: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    // Delete an existing quiz
    const deleteExistingQuiz = async (quizToDelete) => {
        if (!quizToDelete || !quizToDelete.id) {
            alert("Cannot delete quiz: missing ID");
            return;
        }

        // Confirm deletion with the user
        if (!window.confirm("Are you sure you want to delete this quiz? This action cannot be undone.")) {
            return;
        }

        setIsDeleting(true);
        try {
            const response = await axios.post(`${BASELINE}select/gen/topicai`, {
                questionIds: [quizToDelete.id],
                operation: "delete"
            });

            if (response.data && response.data.success) {
                // Remove the deleted quiz from local state
                const updatedQuizzes = existingQuizzes.filter((_, index) => index !== currentCardIndex);
                setExistingQuizzes(updatedQuizzes);

                // Adjust current index if needed
                if (updatedQuizzes.length === 0) {
                    // No more quizzes to show
                    alert("Quiz deleted successfully. No more quizzes available.");
                    setCurrentView('json');
                } else {
                    // Adjust index if we're at the end
                    if (currentCardIndex >= updatedQuizzes.length) {
                        setCurrentCardIndex(updatedQuizzes.length - 1);
                    }
                    alert("Quiz deleted successfully!");
                }
            } else {
                alert("Failed to delete quiz: " + (response.data?.message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error deleting quiz:", error);
            alert(`Error deleting quiz: ${error.message}`);
        } finally {
            setIsDeleting(false);
        }
    };

    // 특정 청크에 대한 API 호출 함수
    const processChunk = async (chunk, chunkIndex, totalChunks) => {
        try {
            // 진행 상태 업데이트
            setChunksStatus(prev => {
                const updated = [...prev];
                updated[chunkIndex] = { status: 'processing', questions: 0 };
                return updated;
            });
            
            // 목표 질문 수 계산 (타겟 갯수 사용시)
            const questionCountInstruction = useTargetCount
                ? `Each chunk should generate approximately ${questionsPerChunk} questions. This chunk should generate questions numbered ${chunkIndex * questionsPerChunk + 1} to ${Math.min((chunkIndex + 1) * questionsPerChunk, targetQuestionCount)}.`
                : '';
            
            // 이 청크에 대한 프롬프트 생성
            const chunkPrompt = `
You are a test author tasked with converting provided HTML content in math problem examples into a structured JSON output. Follow these instructions carefully:

1. Directly transfer the example problems contained in the provided HTML into JSON format exactly as they are, without modifying any wording or numeric values.
2. Each question must be output as a JSON object with the following structure and keys:
   {
     "stem": "…",
     "a": "…",
     "b": "…",
     "c": "…",
     "d": "…",
     "correct_answer": "…",
     "rationale": "…"
   }
3. For multiple-choice questions (when qType is "MCQ"), since the provided HTML does not include answer options, you must generate four answer choices (A, B, C, D) for each question.
   These answer choices must include the correct answer in one of them.
   Ensure that the correct answer is distributed evenly among A, B, C, and D across the set of questions (each option should be correct at least once if possible).
   Additionally, the answer choices should be very similar to the correct answer (e.g., differing only by slight changes in numeric values or wording) so that they are subtly confusing.
4. For free response questions, the "a", "b", "c", and "d" fields should be empty strings.
5. The "stem" should contain a clear, well-worded ${subTopicText} problem that tests student understanding.
6. The "correct_answer" field must be:
   - For MCQ: one of "A", "B", "C", or "D" representing the best answer.
   - For free response: a JSON array containing all valid answers.
7. The "rationale" must begin with ${qType === "MCQ" ? '"Choice [X] is the best answer. Because..."' : '"The correct answer is [X]. Because..."'} and be at least 80 tokens long, thoroughly explaining the solution process.
8. All questions should be independent and based solely on the provided example HTML, with only numeric values changed.
9. **Important:** Any numeric value, mathematical expression, or math function appearing in the "stem", answer choices, or "rationale" must be formatted using LaTeX (KaTeX) syntax (e.g., $5$, $x^2+1$).

${totalChunks > 1 ? `This is part ${chunkIndex + 1} of ${totalChunks} segments being analyzed. Please generate questions only from this segment.` : ''}
${questionCountInstruction}

Below is the HTML input:
--------------------------------------------------
${chunk}
--------------------------------------------------

Now, using the provided HTML input, output exactly in JSON format. Do not include any additional text beside the JSON output.
`;

            // AI API 호출
            const res = await axios.post(`${BASELINE}askai`, { prompt: chunkPrompt, type: "quiz" });

            if (res.data && res.data.message) {
                const answer = res.data.message.trim();
                
                try {
                    // JSON 응답 파싱
                    const parsedQuestions = JSON.parse(answer);
                    
                    // 질문이 배열 형태인지 확인
                    if (Array.isArray(parsedQuestions)) {
                        // 결과 저장 및 진행 상태 업데이트
                        setCombinedResults(prev => [...prev, ...parsedQuestions]);
                        setChunksStatus(prev => {
                            const updated = [...prev];
                            updated[chunkIndex] = { 
                                status: 'success', 
                                questions: parsedQuestions.length 
                            };
                            return updated;
                        });
                        
                        return parsedQuestions;
                    } else {
                        console.error('Unexpected response format for chunk', chunkIndex + 1, answer);
                        setChunksStatus(prev => {
                            const updated = [...prev];
                            updated[chunkIndex] = { status: 'error', error: 'Invalid format' };
                            return updated;
                        });
                        return [];
                    }
                } catch (parseError) {
                    console.error('Error parsing JSON for chunk', chunkIndex + 1, answer, parseError);
                    setChunksStatus(prev => {
                        const updated = [...prev];
                        updated[chunkIndex] = { status: 'error', error: parseError.message };
                        return updated;
                    });
                    return [];
                }
            } else {
                console.error('Unexpected response structure for chunk', chunkIndex + 1, res.data);
                setChunksStatus(prev => {
                    const updated = [...prev];
                    updated[chunkIndex] = { status: 'error', error: 'API response structure error' };
                    return updated;
                });
                return [];
            }
        } catch (error) {
            console.error('API error for chunk', chunkIndex + 1, error);
            setChunksStatus(prev => {
                const updated = [...prev];
                updated[chunkIndex] = { status: 'error', error: error.message };
                return updated;
            });
            return [];
        }
    };

    // 타겟 문제 수와 실제 생성된 문제 수의 차이를 처리하는 함수
    const handleQuestionCountMismatch = async (allResults) => {
        if (!useTargetCount || allResults.length === targetQuestionCount) {
            return allResults; // 타겟 사용 안하거나 정확히 일치하면 그대로 반환
        }
        
        // 현재 생성된 문제가 더 많은 경우
        if (allResults.length > targetQuestionCount) {
            // 목표 갯수까지만 남기고 나머지는 제거
            return allResults.slice(0, targetQuestionCount);
        }
        
        // 현재 생성된 문제가 더 적은 경우, 부족한 만큼 추가 생성 시도
        const missingCount = targetQuestionCount - allResults.length;
        setProcessingStatus(`Generated ${allResults.length} questions. Attempting to generate ${missingCount} more...`);
        
        // 이미 생성된 문제를 기반으로 추가 문제 생성 프롬프트
        const additionalPrompt = `
You are a test author tasked with generating additional math quiz questions. Follow these instructions carefully:

1. You need to generate exactly ${missingCount} more questions in the same style as the existing questions.
2. Each question must be output as a JSON object with the following structure and keys:
   {
     "stem": "…",
     "a": "…",
     "b": "…",
     "c": "…",
     "d": "…",
     "correct_answer": "…",
     "rationale": "…"
   }
3. For multiple-choice questions, generate four answer choices (A, B, C, D) for each question.
   Ensure that the correct answer is distributed evenly among A, B, C, and D across the set of questions.
4. The "stem" should contain a clear, well-worded ${subTopicText} problem that tests student understanding.
5. The "rationale" must begin with "Choice [X] is the best answer. Because..." and thoroughly explain the solution process.
6. All math expressions must be formatted using LaTeX (KaTeX) syntax (e.g., $5$, $x^2+1$).

Create exactly ${missingCount} more questions, similar in style to these examples from the HTML content:
--------------------------------------------------
${htmlContent.substring(0, 1000)}
--------------------------------------------------

Output exactly in JSON format with exactly ${missingCount} questions. Do not include any additional text beside the JSON output.
`;

        try {
            const res = await axios.post(`${BASELINE}askai`, { prompt: additionalPrompt, type: "quiz" });
            
            if (res.data && res.data.message) {
                const answer = res.data.message.trim();
                
                try {
                    const additionalQuestions = JSON.parse(answer);
                    
                    if (Array.isArray(additionalQuestions)) {
                        // 추가 문제를 기존 결과에 병합
                        const combinedQuestions = [...allResults, ...additionalQuestions];
                        setProcessingStatus(`Successfully generated ${combinedQuestions.length} questions in total.`);
                        
                        // 목표 갯수를 초과하는 경우 다시 잘라냄
                        return combinedQuestions.slice(0, targetQuestionCount);
                    }
                } catch (error) {
                    console.error('Error parsing additional questions:', error);
                }
            }
        } catch (error) {
            console.error('Error generating additional questions:', error);
        }
        
        // 추가 생성에 실패한 경우 기존 결과 반환
        return allResults;
    };

    // AI API call to transform HTML content to JSON
    const handleTransform = async () => {
        setIsLoading(true);
        setResult(''); // 이전 결과 초기화
        setCombinedResults([]);
        
        try {
            // HTML을 청크로, 필요한 수만큼 또는 사용자 지정 수로 나눔
            const htmlChunks = useManualChunks 
                ? splitHtmlIntoEqualChunks(htmlContent, manualChunkCount)
                : splitHtmlIntoChunks(htmlContent);
                
            const totalChunks = htmlChunks.length;
            setNumChunks(totalChunks);
            
            // 청크 상태 초기화
            setChunksStatus(new Array(totalChunks).fill({ status: 'pending', questions: 0 }));
            
            // 전체 진행 상태 메시지 표시
            setProcessingStatus(`Processing ${totalChunks} chunks...`);
            
            // 각 청크를 순차적으로 처리
            const allResults = [];
            for (let i = 0; i < totalChunks; i++) {
                const chunkResult = await processChunk(htmlChunks[i], i, totalChunks);
                if (chunkResult) {
                    allResults.push(...chunkResult);
                }
                
                // 진행률 업데이트
                setProcessingStatus(`Processed ${i + 1}/${totalChunks} chunks... (${allResults.length} questions so far)`);
            }
            
            // 목표 문제 수와 다를 경우 추가 처리
            let finalResults = allResults;
            if (useTargetCount && allResults.length !== targetQuestionCount) {
                finalResults = await handleQuestionCountMismatch(allResults);
            }
            
            // 모든 청크의 결과를 합친 최종 JSON 설정
            setResult(JSON.stringify(finalResults, null, 2));
            
            // 질문이 있으면 카드 생성 및 제출 버튼 활성화
            if (finalResults.length > 0) {
                setShowSubmitButton(true);
            }
            
            // 처리 완료 메시지
            setProcessingStatus(`Completed! ${finalResults.length} questions generated.`);
            
        } catch (error) {
            console.error('Error during HTML to JSON conversion:', error);
            setResult(`Error processing content: ${error.message}`);
            setProcessingStatus('Error occurred during processing.');
        }
        
        setIsLoading(false);
    };

    // Parse JSON string to quiz data and switch view to cards
    const handleGenerateQuiz = () => {
        try {
            let parsed;
            try {
                parsed = JSON.parse(result);
            } catch (e) {
                // 주석이나 상태 메시지 제거하고 다시 시도
                const cleanResult = result.replace(/\/\/ .*?\n/g, '');
                parsed = JSON.parse(cleanResult);
            }
            
            if (Array.isArray(parsed)) {
                setQuizData(parsed);
                setCurrentView('cards');
                setCurrentCardIndex(0);
                setShowSubmitButton(true);
            } else {
                alert('The generated result is not an array of questions.');
            }
        } catch (e) {
            alert('Failed to parse JSON. Please try generating again.');
            console.error('JSON parse error:', e);
        }
    };

    // Update specific question (current card)
    const updateQuestion = (updatedQuestion) => {
        if (currentView === 'existing') {
            // For existing quizzes, send to API
            updateExistingQuiz(updatedQuestion);
        } else {
            // For new quizzes, just update state
            setQuizData(prev => {
                const newData = [...prev];
                newData[currentCardIndex] = updatedQuestion;
                return newData;
            });
        }
    };

    // Navigation functions for cards
    const goToNextCard = () => {
        const dataArray = currentView === 'existing' ? existingQuizzes : quizData;
        if (dataArray && currentCardIndex < dataArray.length - 1) {
            setCurrentCardIndex(currentCardIndex + 1);
            setIsKatexRendered(false);
        }
    };

    const goToPrevCard = () => {
        if (currentCardIndex > 0) {
            setCurrentCardIndex(currentCardIndex - 1);
            setIsKatexRendered(false);
        }
    };

    // Reset function to clear data and go back to JSON view
    const handleReset = () => {
        setResult('');
        setQuizData(null);
        setCurrentView('json');
        setCurrentCardIndex(0);
        setShowSubmitButton(false);
        setProcessingStatus('');
        setChunksStatus([]);
        setCombinedResults([]);
    };

    // Regenerate JSON from quizData and switch view to JSON
    const regenerateJSON = () => {
        setResult(JSON.stringify(quizData, null, 2));
        setCurrentView('json');
    };

    // handleAutofillSubmit: Parse JSON result and submit each question to API with enriched fields
    const handleAutofillSubmit = () => {
        try {
            // Use quizData if available (when coming from card view), otherwise parse from result
            const data = quizData || JSON.parse(result);

            if (Array.isArray(data)) {
                const enrichedData = data.map((question) => ({
                    ...question,
                    primary_class_cd_descs: cid, // class 번호 from URL
                    skill_descs: lid,              // lesson 번호 from URL
                    tags: subject,                 // 선택한 subject (math or english)
                    difficultys: "E"
                }));

                enrichedData.forEach((question, index) => {
                    axios
                        .post(BASELINE + "sat/add/mcq/question", question)
                        .then((response) => {
                            // 성공 시 추가 처리 가능
                        })
                        .catch((error) => {
                            console.error(`Error inserting question ${index + 1}:`, error);
                        });
                });

                alert(`${enrichedData.length} questions have been submitted.`);
            } else {
                alert("The provided JSON does not contain an array of questions.");
            }

            onClose();
            setResult('');
            setQuizData(null);
            setShowSubmitButton(false);
        } catch (error) {
            alert("유효하지 않은 JSON 데이터입니다.");
        }
    };

    // Load KaTeX scripts and styles when in card view
    useEffect(() => {
        if (!isOpen || (currentView !== 'cards' && currentView !== 'existing')) return;

        // Load KaTeX CSS
        const linkElement = document.createElement("link");
        linkElement.rel = "stylesheet";
        linkElement.href = "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css";
        linkElement.integrity = "sha384-n8MVd4RsNIU0tAv4ct0nTaAbDJwPJzDEaqSD1odI+WdtXRGWt2kTvGFasHpSy3SV";
        linkElement.crossOrigin = "anonymous";
        document.head.appendChild(linkElement);

        // Load KaTeX auto-render script
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js";
        script.integrity = "sha384-+VBxd3r6XgURycqtZ117nYw44OOcIax56Z4dCRWbxyPt0Koah1uHoK0o4+/RRE05";
        script.crossOrigin = "anonymous";
        script.defer = true;

        script.onload = () => {
            renderKatex();
        };

        document.body.appendChild(script);

        return () => {
            document.head.removeChild(linkElement);
            document.body.removeChild(script);
        };
    }, [isOpen, currentView]);

    // Render KaTeX expressions whenever card content changes
    useEffect(() => {
        if ((currentView === 'cards' || currentView === 'existing') && window.renderMathInElement) {
            renderKatex();
        }
    }, [quizData, existingQuizzes, currentCardIndex, currentView, isKatexRendered]);

    // Function to render KaTeX expressions
    const renderKatex = () => {
        if (!cardRef.current || !window.renderMathInElement) return;

        const elements = cardRef.current.querySelectorAll(".katex-element");
        elements.forEach((element) => {
            window.renderMathInElement(element, {
                delimiters: [
                    { left: "$", right: "$", display: false },
                    { left: "$", right: "$", display: false },
                    { left: "\\(", right: "\\)", display: false },
                    { left: "\\[", right: "\\]", display: true },
                    { left: "\\begin{array}", right: "\\end{array}", display: true },
                ],
                throwOnError: false,
            });
        });
        setIsKatexRendered(true);
    };

    // Quiz Card Component
    const QuizCard = ({ question, onUpdate, onDelete, readOnly = false }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [localData, setLocalData] = useState(question);

        // Update localData when question prop changes
        useEffect(() => {
            setLocalData(question);
        }, [question]);

        const handleChange = (field, value) => {
            setLocalData({ ...localData, [field]: value });
        };

        const handleSave = () => {
            onUpdate(localData);
            setIsEditing(false);
            setIsKatexRendered(false);
        };

        const handleCancel = () => {
            setLocalData(question); // Reset to original data
            setIsEditing(false);
        };

        const isExistingCard = currentView === 'existing';

        return (
            <div className="border p-6 rounded-lg shadow-md bg-white">
                {isEditing ? (
                    <div className="space-y-4">
                        <div>
                            <label className="block font-bold mb-1">Stem:</label>
                            <textarea
                                value={localData.stem}
                                onChange={(e) => handleChange('stem', e.target.value)}
                                className="w-full border p-2 rounded"
                                rows={3}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block font-bold mb-1">Option A:</label>
                                <input
                                    type="text"
                                    value={localData.a}
                                    onChange={(e) => handleChange('a', e.target.value)}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                            <div>
                                <label className="block font-bold mb-1">Option B:</label>
                                <input
                                    type="text"
                                    value={localData.b}
                                    onChange={(e) => handleChange('b', e.target.value)}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                            <div>
                                <label className="block font-bold mb-1">Option C:</label>
                                <input
                                    type="text"
                                    value={localData.c}
                                    onChange={(e) => handleChange('c', e.target.value)}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                            <div>
                                <label className="block font-bold mb-1">Option D:</label>
                                <input
                                    type="text"
                                    value={localData.d}
                                    onChange={(e) => handleChange('d', e.target.value)}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block font-bold mb-1">Correct Answer:</label>
                            <input
                                type="text"
                                value={localData.correct_answer}
                                onChange={(e) => handleChange('correct_answer', e.target.value)}
                                className="w-full border p-2 rounded"
                            />
                        </div>
                        <div>
                            <label className="block font-bold mb-1">Rationale:</label>
                            <textarea
                                value={localData.rationale}
                                onChange={(e) => handleChange('rationale', e.target.value)}
                                className="w-full border p-2 rounded"
                                rows={4}
                            />
                        </div>
                        <div className="flex space-x-4">
                            <button
                                onClick={handleSave}
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                onClick={handleCancel}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
                            >
                                Cancel
                            </button>
                            {isExistingCard && (
                                <button
                                    onClick={() => onDelete && onDelete(localData)}
                                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold text-lg">
                                Question {currentCardIndex + 1} of {
                                    currentView === 'existing' ? existingQuizzes.length : quizData?.length
                                }
                                {isExistingCard && <span className="text-gray-500 text-sm ml-2">(Existing)</span>}
                            </h3>
                            {isExistingCard && localData.id && (
                                <div className="text-xs text-gray-500">
                                    ID: {localData.id}
                                </div>
                            )}
                        </div>
                        <p className="font-bold mt-4">Stem:</p>
                        <p className="bg-gray-50 p-3 rounded katex-element">{localData.stem}</p>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="font-bold">A:</p>
                                <p className="bg-gray-50 p-2 rounded katex-element">{localData.a}</p>
                            </div>
                            <div>
                                <p className="font-bold">B:</p>
                                <p className="bg-gray-50 p-2 rounded katex-element">{localData.b}</p>
                            </div>
                            <div>
                                <p className="font-bold">C:</p>
                                <p className="bg-gray-50 p-2 rounded katex-element">{localData.c}</p>
                            </div>
                            <div>
                                <p className="font-bold">D:</p>
                                <p className="bg-gray-50 p-2 rounded katex-element">{localData.d}</p>
                            </div>
                        </div>
                        <div>
                            <p className="font-bold">Correct Answer:</p>
                            <p className="bg-gray-50 p-2 rounded katex-element">{localData.correct_answer}</p>
                        </div>
                        <div>
                            <p className="font-bold">Rationale:</p>
                            <p className="bg-gray-50 p-2 rounded katex-element">{localData.rationale}</p>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
                            >
                                Edit
                            </button>

                            {isExistingCard && (
                                <button
                                    onClick={() => onDelete && onDelete(localData)}
                                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="text-black fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10 p-4 overflow-auto">
            <div className="bg-white p-6 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Quiz Editor</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* View Tabs */}
                {(quizData || existingQuizzes.length > 0) && (
                    <div className="flex space-x-2 mb-4">
                        <button
                            onClick={() => setCurrentView('json')}
                            className={`px-4 py-2 rounded ${currentView === 'json' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                        >
                            JSON View
                        </button>
                        {quizData && (
                            <button
                                onClick={() => setCurrentView('cards')}
                                className={`px-4 py-2 rounded ${currentView === 'cards' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                            >
                                Card View
                            </button>
                        )}
                        {existingQuizzes.length > 0 && (
                            <button
                                onClick={() => setCurrentView('existing')}
                                className={`px-4 py-2 rounded ${currentView === 'existing' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                            >
                                Existing Quizzes
                            </button>
                        )}
                    </div>
                )}

                {/* Chunking Options & Target Count */}
                {currentView === 'json' && !quizData && (
                    <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
                        <div className="flex flex-col md:flex-row md:space-x-4">
                            {/* Chunking Options */}
                            <div className="mb-4 md:mb-0 md:w-1/2">
                                <h3 className="font-bold mb-2">Chunking Options</h3>
                                <div className="flex items-center space-x-4 mb-2">
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="auto-chunks"
                                            name="chunk-method"
                                            checked={!useManualChunks}
                                            onChange={() => setUseManualChunks(false)}
                                            className="mr-2"
                                        />
                                        <label htmlFor="auto-chunks">Auto-chunking</label>
                                    </div>
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="manual-chunks"
                                            name="chunk-method"
                                            checked={useManualChunks}
                                            onChange={() => setUseManualChunks(true)}
                                            className="mr-2"
                                        />
                                        <label htmlFor="manual-chunks">Manual chunks</label>
                                    </div>
                                </div>
                                
                                {useManualChunks && (
                                    <div className="flex items-center">
                                        <label htmlFor="chunk-count" className="mr-2">Number of chunks:</label>
                                        <input 
                                            type="number" 
                                            id="chunk-count"
                                            value={manualChunkCount} 
                                            onChange={(e) => setManualChunkCount(Math.max(1, parseInt(e.target.value) || 1))}
                                            min="1"
                                            max="10"
                                            className="border rounded p-1 w-20"
                                        />
                                    </div>
                                )}
                            </div>
                            
                            {/* Target Question Count Options */}
                            <div className="md:w-1/2">
                                <h3 className="font-bold mb-2">Target Questions</h3>
                                <div className="flex items-center mb-2">
                                    <input
                                        type="checkbox"
                                        id="use-target-count"
                                        checked={useTargetCount}
                                        onChange={(e) => setUseTargetCount(e.target.checked)}
                                        className="mr-2"
                                    />
                                    <label htmlFor="use-target-count">Specify exact number of questions</label>
                                </div>
                                
                                {useTargetCount && (
                                    <div className="flex items-center">
                                        <label htmlFor="target-question-count" className="mr-2">Target questions:</label>
                                        <input 
                                            type="number" 
                                            id="target-question-count"
                                            value={targetQuestionCount} 
                                            onChange={(e) => setTargetQuestionCount(Math.max(1, parseInt(e.target.value) || 1))}
                                            min="1"
                                            max="100"
                                            className="border rounded p-1 w-20"
                                        />
                                    </div>
                                )}
                                
                                {useManualChunks && useTargetCount && questionsPerChunk > 0 && (
                                    <div className="mt-2 text-sm text-gray-600">
                                        Each chunk will generate ~{questionsPerChunk} questions
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {currentView === 'json' && !quizData && (
                        <>
                            <button
                                onClick={handleTransform}
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Generating...' : 'Generate JSON'}
                            </button>
                            <button
                                onClick={fetchExistingQuizzes}
                                className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 transition"
                                disabled={isLoadingExisting}
                            >
                                {isLoadingExisting ? 'Loading...' : 'Fetch Existing Quizzes'}
                            </button>
                            {result && (
                                <button
                                    onClick={handleGenerateQuiz}
                                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition"
                                >
                                    Create Cards
                                </button>
                            )}
                        </>
                    )}

                    {currentView === 'cards' && (
                        <>
                            <button
                                onClick={regenerateJSON}
                                className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition"
                            >
                                Back to JSON
                            </button>

                            {showSubmitButton && (
                                <button
                                    onClick={handleAutofillSubmit}
                                    className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition"
                                >
                                    Submit Quiz
                                </button>
                            )}
                        </>
                    )}

                    {currentView === 'existing' && (
                        <button
                            onClick={() => setCurrentView('json')}
                            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                        >
                            Back to Editor
                        </button>
                    )}

                    <button
                        onClick={handleReset}
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                    >
                        Reset
                    </button>
                </div>

                {/* Processing Status Display */}
                {processingStatus && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-blue-700 flex items-center">
                            {isLoading && (
                                <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            )}
                            {processingStatus}
                        </p>
                        
                        {/* Chunk Status Display */}
                        {chunksStatus.length > 0 && (
                            <div className="mt-2">
                                <h4 className="font-bold text-sm mb-1">Chunk Status:</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {chunksStatus.map((chunk, index) => (
                                        <div key={index} className={`p-2 rounded text-sm ${
                                            chunk.status === 'pending' ? 'bg-gray-100 text-gray-600' :
                                            chunk.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                                            chunk.status === 'success' ? 'bg-green-100 text-green-800' :
                                            'bg-red-100 text-red-800'
                                        }`}>
                                            <span className="font-medium">Chunk {index + 1}:</span> {
                                                chunk.status === 'pending' ? 'Waiting...' :
                                                chunk.status === 'processing' ? 'Processing...' :
                                                chunk.status === 'success' ? `${chunk.questions} questions generated` :
                                                `Error: ${chunk.error}`
                                            }
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Content Area */}
                {currentView === 'json' && (
                    <div className="mb-4">
                        <textarea
                            className="w-full h-64 bg-gray-50 p-4 rounded border font-mono text-sm"
                            value={result}
                            onChange={(e) => setResult(e.target.value)}
                            placeholder="JSON will appear here after generation"
                        />
                        <div className="flex justify-between items-center mt-2">
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(result)
                                        .then(() => alert('Copied to clipboard!'))
                                        .catch((err) => alert('Failed to copy: ' + err));
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
                            >
                                Copy JSON
                            </button>

                            {showSubmitButton && (
                                <button
                                    onClick={handleAutofillSubmit}
                                    className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition"
                                >
                                    Submit Quiz
                                </button>
                            )}
                        </div>

                        <div className="mt-4">
                            <label className="font-bold mb-1 block">Subject (math or english):</label>
                            <select
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                className="w-full border p-2 rounded"
                            >
                                <option value="math">Math</option>
                                <option value="english">English</option>
                            </select>
                        </div>
                    </div>
                )}

                {currentView === 'cards' && quizData && (
                    <div ref={cardRef}>
                        <div className="mb-4">
                            <QuizCard
                                question={quizData[currentCardIndex]}
                                onUpdate={updateQuestion}
                                readOnly={false}
                            />
                        </div>

                        <div className="flex justify-between items-center mt-6">
                            <button
                                onClick={goToPrevCard}
                                disabled={currentCardIndex === 0}
                                className={`px-4 py-2 rounded ${currentCardIndex === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'} transition`}
                            >
                                <span className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Previous
                                </span>
                            </button>

                            <span className="text-gray-600">
                                {currentCardIndex + 1} of {quizData.length}
                            </span>

                            <button
                                onClick={goToNextCard}
                                disabled={currentCardIndex === quizData.length - 1}
                                className={`px-4 py-2 rounded ${currentCardIndex === quizData.length - 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'} transition`}
                            >
                                <span className="flex items-center">
                                    Next
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                    </svg>
                                </span>
                            </button>
                        </div>
                    </div>
                )}

                {currentView === 'existing' && existingQuizzes.length > 0 && (
                    <div ref={cardRef}>
                        <div className="mb-4">
                            <QuizCard
                                question={existingQuizzes[currentCardIndex]}
                                onUpdate={updateExistingQuiz}
                                onDelete={deleteExistingQuiz}
                                readOnly={false}
                            />
                        </div>

                        <div className="flex justify-between items-center mt-6">
                            <button
                                onClick={goToPrevCard}
                                disabled={currentCardIndex === 0}
                                className={`px-4 py-2 rounded ${currentCardIndex === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'} transition`}
                            >
                                <span className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Previous
                                </span>
                            </button>

                            <span className="text-gray-600">
                                {currentCardIndex + 1} of {existingQuizzes.length}
                            </span>

                            <button
                                onClick={goToNextCard}
                                disabled={currentCardIndex === existingQuizzes.length - 1}
                                className={`px-4 py-2 rounded ${currentCardIndex === existingQuizzes.length - 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'} transition`}
                            >
                                <span className="flex items-center">
                                    Next
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                    </svg>
                                </span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuizEditModal;